import { APIError } from '@repo/errors/errors';
import { TOKEN_COOKIE_NAME } from './auth/constants';
import { refreshTokenWithTimeout } from './auth/refresh-token-with-timeout';
import { readClientCookie } from './utils/cookie-utils';

export interface HttpRequestOptions extends Omit<RequestInit, 'method' | 'body'> {
    readonly params?: Record<string, string | string[] | number | boolean>;
    readonly body?: JsonValue | FormData;
    readonly name?: string;
    readonly collectMetrics?: boolean;
    readonly skipAutoAuth?: boolean;
    readonly retry404?: boolean;
}

export type JsonValue = object | readonly JsonValue[] | string | number | null;

export type UrlParams = Record<string, string | string[]>;

let _tokenOverride: string | null = null;
let _onTokenExpired: (() => Promise<string | null>) | null = null;
let _refreshPromise: Promise<string | null> | null = null;

export function setTokenOverride(token: string | null): void {
    _tokenOverride = token;
}

export function setOnTokenExpired(handler: (() => Promise<string | null>) | null): void {
    _onTokenExpired = handler;
}

export class WeModApiClient {
    readonly #baseUrl: string;

    public constructor(baseUrl: string) {
        this.#baseUrl = baseUrl;
    }

    public get baseUrl(): string {
        return this.#baseUrl;
    }

    public get<T = unknown>(endpoint: string, options?: HttpRequestOptions): Promise<T> {
        return this.fetch<T>(endpoint, {
            ...options,
            method: 'GET',
        });
    }

    public post<T = unknown>(endpoint: string, body?: JsonValue | FormData, options?: HttpRequestOptions): Promise<T> {
        return this.fetch<T>(endpoint, {
            ...options,
            body,
            method: 'POST',
        });
    }

    public put<T = unknown>(endpoint: string, body?: JsonValue | FormData, options?: HttpRequestOptions): Promise<T> {
        return this.fetch<T>(endpoint, {
            ...options,
            body,
            method: 'PUT',
        });
    }

    public patch<T = unknown>(endpoint: string, body?: JsonValue | FormData, options?: HttpRequestOptions): Promise<T> {
        return this.fetch<T>(endpoint, {
            ...options,
            body,
            method: 'PATCH',
        });
    }

    public delete<T = unknown>(endpoint: string, options?: HttpRequestOptions): Promise<T> {
        return this.fetch<T>(endpoint, {
            ...options,
            method: 'DELETE',
        });
    }

    public async fetch<T = unknown>(endpoint: string, options: HttpRequestOptions & { method: string }): Promise<T> {
        const locale = (options.headers as Record<string, string>)?.['Accept-Language'];

        const headers: Record<string, string> = {
            Accept: 'application/json',
            ...((options.headers as Record<string, string>) || {}),
        };

        if (locale !== undefined) {
            headers['Accept-Language'] = locale;
        }

        if (!headers.Authorization && !options.skipAutoAuth) {
            const token = _tokenOverride ?? readClientCookie(TOKEN_COOKIE_NAME);
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }

        const fetchOptions: RequestInit = {
            ...options,
            method: options.method,
            headers,
            body: undefined,
        };

        if (options.body !== undefined) {
            if (options.body instanceof FormData) {
                fetchOptions.body = options.body;
            } else {
                headers['Content-Type'] = 'application/json';
                fetchOptions.body = JSON.stringify(options.body) as BodyInit;
            }
        }

        let url = endpoint.startsWith('http') ? endpoint : `${this.#baseUrl}${endpoint}`;

        if (options.params) {
            const queryString = this.#buildQueryString(this.#normalizeParams(options.params));
            url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
        }

        let response = await fetch(url, fetchOptions);

        if (response.status === 404 && options.retry404 && !headers.Authorization) {
            // Get token and retry once
            const token = _tokenOverride ?? readClientCookie(TOKEN_COOKIE_NAME);
            if (token) {
                headers.Authorization = `Bearer ${token}`;
                const retryOptions = { ...fetchOptions, headers };
                response = await fetch(url, retryOptions);
            }
        }

        if (response.status === 401) {
            // Snapshot before any async work so concurrent 401s see consistent state
            const hadTokenOverride = _tokenOverride !== null;
            const hadOnTokenExpired = _onTokenExpired;

            if (!_refreshPromise) {
                _refreshPromise = (async () => {
                    try {
                        if (hadTokenOverride && hadOnTokenExpired) {
                            _tokenOverride = null;
                            const newToken = await hadOnTokenExpired();
                            if (newToken) {
                                _tokenOverride = newToken;
                            }
                            return newToken;
                        }

                        _tokenOverride = null;
                        if (typeof document !== 'undefined') {
                            const urlObj = new URL(process.env.NEXT_PUBLIC_WAND_WEBSITE_URL || window.location.origin);
                            const hostname = urlObj.hostname;
                            const isSharedDomain = hostname.includes('.wemod.');
                            const domain = isSharedDomain ? `; domain=.${hostname}` : '';
                            // biome-ignore lint/suspicious/noDocumentCookie: TODO: migrate to library / cookie api
                            document.cookie = `token=; path=/;${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                        }
                        const result = await refreshTokenWithTimeout({ force: true });
                        return result.success ? result.token : null;
                    } catch {
                        return null;
                    } finally {
                        _refreshPromise = null;
                    }
                })();
            }

            const refreshedToken = await _refreshPromise;
            if (refreshedToken) {
                headers.Authorization = `Bearer ${refreshedToken}`;
                const retryOptions = { ...fetchOptions, headers };
                response = await fetch(url, retryOptions);
            }
        }

        if (!response.ok) {
            // Don't log 401s as errors - they're expected when not authenticated
            if (response.status !== 401) {
                console.error(`HTTP error: ${url} ${response.status} ${response.statusText}`);
            }

            throw new APIError(response.statusText, {
                status: response.status,
            });
        }

        return await this.#parseResponse(response);
    }

    #buildQueryString(params: UrlParams): string {
        return Object.entries(params)
            .map(([key, value]) => {
                if (Array.isArray(value)) {
                    return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`).join('&');
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
            })
            .join('&');
    }

    #normalizeParams(params: Record<string, string | string[] | number | boolean>): UrlParams {
        return Object.entries(params).reduce(
            (acc, [key, value]) => {
                acc[key] = String(value);
                return acc;
            },
            {} as Record<string, string | string[]>,
        );
    }

    async #parseResponse<T>(response: Response): Promise<T> {
        return response.headers.get('Content-Type')?.includes('application/json')
            ? ((await response.json()) as T)
            : ((await response.text()) as T);
    }
}
